/**
 * Iter 44 — Compact "Message provider" trigger button.
 *
 * FP3-069 follow-up #14 — Supervision-aware recipient resolution.
 *
 * When `context.student_id` is provided, the button asks the backend
 * for the recipient candidates for the (student, caller) pair via
 * `/api/messages/recipient-candidates`. That endpoint returns
 * supervision-aware defaults (e.g. Pete clicking Message on a goal
 * for a Max-assigned student gets Max as the default recipient, not
 * himself). The dialog opens with the picker pre-populated.
 *
 * When no student context is supplied (legacy admin surfaces), the
 * button falls back to the single-recipient `provider` prop behavior.
 */
import { useEffect, useState } from "react";
import { PaperPlaneTilt } from "@phosphor-icons/react";
import { MessageComposerDialog } from "./MessageComposerDialog";
import api from "@/lib/api";

export function MessageProviderButton({
  provider,
  context,
  size = "sm",
  variant = "ghost",
  label = "Message",
  testid = "message-provider-btn",
  onSent,
  disabled = false,
}) {
  const [open, setOpen] = useState(false);
  // FP3-069 follow-up #14 — Async-loaded supervision-aware candidates.
  // `null` = not yet loaded; `[]` = backend said no candidates.
  const [candidates, setCandidates] = useState(null);
  const [channel, setChannel] = useState("admin_note");
  const [loadingCandidates, setLoadingCandidates] = useState(false);

  // Fetch candidates once the button is opened AND we have a student
  // in the context. Avoid eagerly fetching on every render of every
  // row (admin lists, progress tab, etc.) — only at click time.
  useEffect(() => {
    if (!open) return;
    if (!context?.student_id) {
      // No student context — fall back to the legacy single-recipient
      // path using the supplied `provider` prop.
      setCandidates(null);
      setChannel("admin_note");
      return;
    }
    let cancelled = false;
    setLoadingCandidates(true);
    api.getMessageRecipientCandidates({ student_id: context.student_id })
      .then((r) => {
        if (cancelled) return;
        setCandidates(r.data?.candidates || []);
        setChannel(r.data?.channel || "admin_note");
      })
      .catch(() => {
        if (cancelled) return;
        setCandidates(null);
        setChannel("admin_note");
      })
      .finally(() => {
        if (!cancelled) setLoadingCandidates(false);
      });
    return () => { cancelled = true; };
  }, [open, context?.student_id]);

  const sizing =
    size === "xs"
      ? "text-[10px] px-1.5 py-1 gap-1"
      : size === "sm"
        ? "text-[11px] px-2 py-1 gap-1.5"
        : "text-[12px] px-2.5 py-1.5 gap-1.5";
  const variantClass =
    variant === "solid"
      ? "bg-[#1E3F33] text-white hover:bg-[#18322A] border border-transparent"
      : variant === "outline"
        ? "bg-white text-[#3F4756] border border-[#E5E3DB] hover:border-[#3F4756]/40 hover:bg-[#F3F1EC]"
        : "text-[#3F4756] hover:text-[#1E3F33] hover:bg-[#E8F0EB] border border-transparent";

  const iconSize = size === "xs" ? 10 : size === "sm" ? 11 : 13;
  const isDisabled = disabled || (!provider?.id && !context?.student_id);

  return (
    <>
      <button
        type="button"
        onClick={(e) => {
          e.stopPropagation();
          if (!isDisabled) setOpen(true);
        }}
        data-testid={testid}
        disabled={isDisabled}
        title={
          isDisabled
            ? "No recipient available"
            : `Message about ${context?.student_name || provider?.name || "this"}`
        }
        className={`inline-flex items-center font-medium rounded-md transition-colors ${sizing} ${variantClass} ${
          isDisabled ? "opacity-40 cursor-not-allowed" : ""
        }`}
      >
        <PaperPlaneTilt size={iconSize} weight="duotone" />
        {label}
      </button>
      <MessageComposerDialog
        open={open && !loadingCandidates}
        onOpenChange={setOpen}
        provider={provider}
        candidates={candidates}
        channel={channel}
        context={context}
        onSent={onSent}
      />
    </>
  );
}
